import React, { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigationItems } from '../../../constants/navigationItems';
import skybizLogo from '../../../assets/icon/skybiz.png';
import { useAuth } from '../../../Context/AuthContext';
import ProfileDropdown from './ProfileDropdown';
import WhatsAppIcon from '../../../assets/icon/whatsappIcon';
import useBgColorStore from '../../../store/bgColor';
import LightSunIcon from '../../../assets/lightSunIcon';
import MoonIcon from '../../../assets/MoonIcon';
import { useTheme } from '../../../useTheme';
import CrossIconHeader from '../../../assets/icon/CrossIcon';
import { useRef, useEffect } from 'react';
import HamBurgerIcons from '../../../assets/HamburgerIcon';
import CrossIconHeaderDark from '../../../assets/icon/CrossIconDark';
import { showOrReplaceToast } from '../../../utils/utility';

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { bgColor, updateBgColor } = useBgColorStore();
  const isDarkMode = theme === 'dark';
  const [menuOpen, setMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname],
  );
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);
  const handleClick = () => {
    const fallbackUrl = `https://web.whatsapp.com/`;
    const whatsappUrl = `whatsapp://send`;
    showOrReplaceToast(
      'whatsapp',
      'If WhatsApp doesn’t open, please make sure the app is installed and running in background',
      'warning',
    );
    const timeout = setTimeout(() => {
      window.open(fallbackUrl, '_blank');
    }, 1500);
    window.location.href = whatsappUrl;
    window.addEventListener('blur', () => clearTimeout(timeout));
  };
  return (
    <header
      className={` bg-sidebar-background shadow-sm fixed  z-50 w-full lg:w-auto`}
    >
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center px-4 py-2 border-b">
        <button
          onClick={toggleMenu}
          className="p-2 rounded-lg transition-colors duration-200"
          aria-label="Toggle Menu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <div>
              <div className="block dark:hidden">
                <HamBurgerIcons fill="#000000" />
              </div>
              <div className="hidden dark:block">
                <HamBurgerIcons fill="#FFFFFF" />
              </div>
            </div>
          ) : (
            <div>
              <div className="block dark:hidden">
                <HamBurgerIcons fill="#000000" />
              </div>
              <div className="hidden dark:block">
                <HamBurgerIcons fill="#FFFFFF" />
              </div>
            </div>
          )}
        </button>
        <div className="flex w-full items-center justify-center">
          <img
            className={menuOpen ? `w-24 hidden sm:block` : `w-24`}
            src={skybizLogo}
            alt="Skybiz Logo"
          />
        </div>
        <div className="mr-3">
          <button onClick={handleClick}>
            <WhatsAppIcon width={35} height={35} />
          </button>
        </div>
        <ProfileDropdown />
      </div>

      {/* Mobile Side Menu */}
      <div
        ref={sidebarRef}
        className={`top-0 shadow-sm fixed z-50 left-0 h-screen bg-sidebar-background transform ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 w-fit`}
      >
        <div className="p-4 flex justify-between items-center ">
          <img className="w-24" src={skybizLogo} alt="Skybiz Logo" />

          <button
            onClick={toggleMenu}
            className="p-2 rounded-lg transition-colors duration-200"
            aria-label="Close Menu"
          >
            <div>
              <div className="block dark:hidden">
                <CrossIconHeader />
              </div>
              <div className="hidden dark:block">
                <CrossIconHeaderDark />
              </div>
            </div>
          </button>
        </div>

        <div className="flex items-center justify-end gap-4 w-full max-w-xs px-4 py-2">
          <div className="flex items-center space-x-2">
            <span className="text-xl">
              {isDarkMode ? <MoonIcon /> : <LightSunIcon />}
            </span>
            <span className="text-text-lightGray text-sm font-medium">
              {isDarkMode ? 'Dark' : 'Light'}
            </span>
          </div>

          {/* Toggle Switch */}
          <div
            onClick={toggleTheme}
            className={`w-[54px] h-[32px] flex items-center bg-backgroundColor-toggleButtonColor rounded-full p-1 cursor-pointer transition-all duration-300 `}
          >
            <div
              className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${
                isDarkMode ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </div>
        </div>

        <div className="border-b border-customBorder-sidebarLineColor"></div>

        <nav className="mt-4">
          <ul className="space-y-4 p-4">
            {navigationItems.map((item) => (
              <li
                key={item.id}
                className={`${item.showOnMobile ? 'block sm:hidden' : 'block'}`}
              >
                <Link to={item.url} onClick={toggleMenu}>
                  <button
                    className={`w-full p-4 flex items-between gap-4 rounded-lg transition-colors duration-200 text-textColorGray
          ${
            isActive(item.url)
              ? isDarkMode
                ? 'bg-black'
                : `${bgColor}`
              : 'text-textColorGray'
          }
        `}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Desktop Sidebar */}
      <div
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className={`hidden lg:flex lg:flex-col min-h-screen text-sidebar-textColor transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-64' : 'w-20'
        }`}
      >
        <div className="p-4 flex justify-between items-center border-b">
          {isExpanded && (
            <h2 className="text-xl font-bold">
              <img className="w-3/4" src={skybizLogo} alt="Skybiz Logo" />
            </h2>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg transition-colors duration-200"
            aria-label={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <div>
                <div className="block dark:hidden">
                  <HamBurgerIcons fill="#000000" />
                </div>
                <div className="hidden dark:block">
                  <HamBurgerIcons fill="#FFFFFF" />
                </div>
              </div>
            ) : (
              <div>
                <div className="block dark:hidden">
                  <HamBurgerIcons fill="#000000" />
                </div>
                <div className="hidden dark:block">
                  <HamBurgerIcons fill="#FFFFFF" />
                </div>
              </div>
            )}
          </button>
        </div>

        {/* Theme Toggle */}
        {isExpanded && (
          <div className="flex items-center justify-end gap-4 w-full px-4 py-2">
            <div className="flex items-center space-x-2">
              <span className="text-xl">
                {isDarkMode ? <MoonIcon /> : <LightSunIcon />}
              </span>
              <span className="text-text-lightGray text-sm font-medium">
                {isDarkMode ? 'Dark' : 'Light'}
              </span>
            </div>

            <div
              onClick={toggleTheme}
              className={`w-[54px] h-[32px] flex items-center bg-backgroundColor-toggleButtonColor rounded-full p-1 cursor-pointer transition-all duration-300 `}
            >
              <div
                className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 mt-4">
          <ul className="space-y-2">
            {navigationItems.map((item) => (
              <li
                key={item.id}
                className={`${item.showOnMobile ? 'block sm:hidden' : 'block'}`}
              >
                <Link
                  to={item.url}
                  onClick={toggleSidebar}
                  className={`w-full p-4 flex items-center rounded-lg 
          ${isExpanded ? 'justify-start' : 'justify-center'}
          ${isActive(item.url) ? (isDarkMode ? 'bg-black' : bgColor) : ''}`}
                >
                  {item.icon}
                  {isExpanded && (
                    <span className="ml-4 text-lg">{item.label}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
